
(() => {
  const DRAFT_KEY = 'contact_tech_draft';
  const DRAFT_TTL_MS = 60 * 60 * 1000;
  const MAX_FILES = 5;
  const MAX_FILE_BYTES = 10 * 1024 * 1024;

  /**
   * English fallbacks. The live copy comes from the [data-contact-i18n] block the
   * snippet renders, so every string can be translated in Translate & Adapt.
   */
  const DEFAULT_ERRORS = {
    title_required: 'Please add a title.',
    body_required: 'Please write your question.',
    category_id_required: 'Please pick a category.',
    category_not_found: 'That category is unavailable. Pick another.',
    consent_required: 'Please consent to continue.',
    too_many_files: 'You can attach at most [count] files.',
    file_too_large: 'One of your files is larger than 10 MB.',
    file_too_large_named: '[filename] is larger than 10 MB.',
    invalid_multipart: 'Could not send your message. Please try again.',
    discourse_validation_failed: 'Your post was rejected by the forum. Please adjust and try again.',
    discourse_rate_limited: 'Too many posts. Please try again later.',
    invalid_topic: 'Please pick a topic.',
    name_required: 'Please add your name.',
    invalid_email: 'Enter a valid email address.',
    message_required: 'Please write a message.',
    turnstile_failed: 'Could not verify you are human. Please retry.',
    both_forum_and_email_failed: 'Could not send your message. Please try again later.',
    send_failed: 'Could not send your message. Please try again later.',
    categories_unavailable: 'Could not load categories. Please refresh the page.',
    network: 'Network error. Please check your connection and try again.',
    rate_limited_one: 'Too many submissions. Please try again in [count] minute.',
    rate_limited_other: 'Too many submissions. Please try again in [count] minutes.',
  };

  const DEFAULT_UI = {
    category_placeholder: 'Select a category…',
    category_failed: 'Could not load categories',
    file_remove: 'Remove',
    tech_submit: 'POST TO SOLDERED COMMUNITY',
    tech_submit_loading: 'POSTING…',
    std_submit: 'SEND',
    std_submit_loading: 'SENDING…',
  };

  /**
   * @param {HTMLElement} root
   * @returns {{ errors: Record<string, string>, ui: Record<string, string> }}
   */
  function readI18n(root) {
    const el = root.querySelector('[data-contact-i18n]');
    let parsed = null;
    if (el && el.textContent) {
      try { parsed = JSON.parse(el.textContent); } catch (_) { /* fall back to English */ }
    }
    return {
      errors: Object.assign({}, DEFAULT_ERRORS, (parsed && parsed.errors) || {}),
      ui: Object.assign({}, DEFAULT_UI, (parsed && parsed.ui) || {}),
    };
  }

  /**
   * Fills the [token] placeholders the snippet leaves behind for us, the same way
   * assets/localization.js handles '[count]'.
   * @param {string} template
   * @param {Record<string, string | number>} vars
   */
  function interpolate(template, vars) {
    return String(template).replace(/\[(\w+)\]/g, (match, key) => (
      Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match
    ));
  }

  const ICON_CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="4 12 10 18 20 6"/></svg>';
  const ICON_WARN = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 9v4"/><path d="M12 17h.01"/><path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/></svg>';

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    const rootEl = document.querySelector('.contact-v5');
    if (!rootEl) return;
    const root = /** @type {HTMLElement} */ (rootEl);

    const bridge = root.dataset.bridgeOrigin || 'https://bridge.soldered.com';
    const communityUrl = root.dataset.communityUrl || 'https://community.soldered.com/';

    const { errors: ERROR_COPY, ui: UI } = readI18n(root);

    const choices = /** @type {NodeListOf<HTMLElement>} */ (root.querySelectorAll('[data-choice]'));
    const panels = /** @type {NodeListOf<HTMLElement>} */ (root.querySelectorAll('[data-panel]'));
    const techForm = /** @type {HTMLFormElement | null} */ (root.querySelector('[data-form="tech"]'));
    const stdForm = /** @type {HTMLFormElement | null} */ (root.querySelector('[data-form="standard"]'));
    const categorySelect = /** @type {HTMLSelectElement | null} */ (root.querySelector('#tech-category'));
    const fileInput = /** @type {HTMLInputElement | null} */ (root.querySelector('#tech-attachments'));
    const fileList = /** @type {HTMLElement | null} */ (root.querySelector('[data-file-list]'));
    const dropzone = /** @type {HTMLElement | null} */ (root.querySelector('[data-dropzone]'));
    const draftBanner = /** @type {HTMLElement | null} */ (root.querySelector('[data-draft-banner]'));
    const topicLink = /** @type {HTMLAnchorElement | null} */ (root.querySelector('[data-topic-link]'));
    const choiceBlock = /** @type {HTMLElement | null} */ (root.querySelector('[data-step="choice"]'));

    /** @type {File[]} */
    let selectedFiles = [];

    choices.forEach((el) => {
      el.addEventListener('click', () => {
        const type = el.getAttribute('data-choice');
        if (type) selectPath(type);
      });
    });

    root.querySelectorAll('[data-reset]').forEach((btn) => {
      btn.addEventListener('click', resetToChoice);
    });

    /** @param {string} name */
    function showPanel(name) {
      panels.forEach((p) => {
        p.hidden = p.getAttribute('data-panel') !== name;
      });
      const target = /** @type {HTMLElement | null} */ (root.querySelector(`[data-panel="${name}"]`));
      if (target) {
        setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 30);
      }
    }

    /** @param {string} type */
    function selectPath(type) {
      choices.forEach((el) => {
        el.classList.toggle('is-active', el.getAttribute('data-choice') === type);
      });
      if (choiceBlock) choiceBlock.hidden = false;
      showPanel(type);
    }

    function resetToChoice() {
      choices.forEach((el) => el.classList.remove('is-active'));
      panels.forEach((p) => (p.hidden = true));
      clearFormBanners();
      if (choiceBlock) {
        choiceBlock.hidden = false;
        choiceBlock.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    function clearFormBanners() {
      root.querySelectorAll('[data-form-error]').forEach((el) => {
        const html = /** @type {HTMLElement} */ (el);
        html.hidden = true;
        html.textContent = '';
      });
      root.querySelectorAll('[data-error-for]').forEach((el) => {
        const html = /** @type {HTMLElement} */ (el);
        html.hidden = true;
        html.textContent = '';
      });
      root.querySelectorAll('[aria-invalid="true"]').forEach((el) => el.removeAttribute('aria-invalid'));
    }

    /**
     * @param {HTMLFormElement} form
     * @param {string} fieldId
     * @param {string} message
     */
    function showFieldError(form, fieldId, message) {
      const field = form.querySelector(`#${fieldId}`);
      const err = /** @type {HTMLElement | null} */ (form.querySelector(`[data-error-for="${fieldId}"]`));
      if (field) field.setAttribute('aria-invalid', 'true');
      if (err) {
        err.textContent = message;
        err.hidden = false;
      }
    }

    /**
     * @param {HTMLFormElement} form
     * @param {string} message
     */
    function showFormBanner(form, message) {
      const banner = /** @type {HTMLElement | null} */ (form.querySelector('[data-form-error]'));
      if (banner) {
        banner.textContent = message;
        banner.hidden = false;
        banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    /**
     * @param {string | null | undefined} error
     * @param {Record<string, unknown> | null} [extra]
     */
    function mapError(error, extra) {
      if (error === 'rate_limited' && extra && typeof extra.retry_after === 'number') {
        const mins = Math.max(1, Math.ceil(extra.retry_after / 60));
        const copy = mins === 1 ? ERROR_COPY.rate_limited_one : ERROR_COPY.rate_limited_other;
        return interpolate(copy, { count: mins });
      }
      if (error) {
        const v = /** @type {Record<string, string | undefined>} */ (ERROR_COPY)[error];
        if (v) return v;
      }
      return ERROR_COPY.network;
    }

    /* ---------- categories ---------- */

    loadCategories();

    async function loadCategories() {
      if (!categorySelect) return;
      try {
        const res = await fetch(`${bridge}/api/categories`, { credentials: 'include' });
        if (!res.ok) throw new Error('categories_unavailable');
        const data = await res.json();
        if (!data || !Array.isArray(data.categories)) throw new Error('categories_unavailable');
        categorySelect.innerHTML = '';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = UI.category_placeholder;
        categorySelect.appendChild(placeholder);
        /** @type {{ id: number, slug: string, name: string }[]} */
        const cats = data.categories;
        cats.forEach((cat) => {
          const opt = document.createElement('option');
          opt.value = String(cat.id);
          opt.textContent = cat.name;
          categorySelect.appendChild(opt);
        });

        const draft = readDraft();
        if (draft && draft.category_id) categorySelect.value = String(draft.category_id);
      } catch (_e) {
        categorySelect.innerHTML = '';
        const failed = document.createElement('option');
        failed.value = '';
        failed.textContent = UI.category_failed;
        categorySelect.appendChild(failed);
      }
    }

    /* ---------- draft restore (after login redirect) ---------- */

    handleReturnFromLogin();

    async function handleReturnFromLogin() {
      const params = new URLSearchParams(window.location.search);
      if (params.get('from') !== 'login') return;

      params.delete('from');
      const newSearch = params.toString();
      const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', newUrl);

      const draft = readDraft();
      if (!draft || !techForm) return;

      setFieldValue('tech-title', draft.title || '');
      setFieldValue('tech-body', draft.body || '');
      const notify = /** @type {HTMLInputElement | null} */ (root.querySelector('#tech-notify'));
      if (notify) notify.checked = draft.notify !== false;
      const consent = /** @type {HTMLInputElement | null} */ (root.querySelector('#tech-consent'));
      if (consent) consent.checked = !!draft.consent;
      selectPath('tech');

      let signedIn = false;
      try {
        const res = await fetch(`${bridge}/api/whoami`, { credentials: 'include' });
        const data = await safeJson(res);
        signedIn = !!(res.ok && data && data.signed_in);
      } catch (_) { /* network — assume not signed in */ }

      renderDraftBanner(signedIn, Array.isArray(draft.file_names) && draft.file_names.length > 0);

      if (signedIn) highlightSubmit();
    }

    /**
     * @param {boolean} signedIn
     * @param {boolean} hadFiles
     */
    function renderDraftBanner(signedIn, hadFiles) {
      if (!draftBanner) return;

      const ds = draftBanner.dataset;
      const title = signedIn ? (ds.i18nSignedTitle || '') : (ds.i18nUnsignedTitle || '');
      let body = signedIn ? (ds.i18nSignedBody || '') : (ds.i18nUnsignedBody || '');
      if (signedIn && hadFiles && ds.i18nSignedFiles) body = `${body} ${ds.i18nSignedFiles}`;

      const titleEl = /** @type {HTMLElement | null} */ (draftBanner.querySelector('[data-draft-title]'));
      const bodyEl = /** @type {HTMLElement | null} */ (draftBanner.querySelector('[data-draft-body]'));
      const iconEl = /** @type {HTMLElement | null} */ (draftBanner.querySelector('[data-draft-icon]'));
      if (titleEl) titleEl.textContent = title;
      if (bodyEl) bodyEl.textContent = body;
      if (iconEl) iconEl.innerHTML = signedIn ? ICON_CHECK : ICON_WARN;

      draftBanner.classList.toggle('contact-v5__draft-banner--ok', signedIn);
      draftBanner.classList.toggle('contact-v5__draft-banner--warn', !signedIn);
      draftBanner.hidden = false;
    }

    function highlightSubmit() {
      if (!techForm) return;
      const submitBtn = /** @type {HTMLButtonElement | null} */ (techForm.querySelector('[data-submit]'));
      if (!submitBtn) return;

      setTimeout(() => {
        submitBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
        submitBtn.classList.add('is-highlight');
      }, 250);

      const stop = () => submitBtn.classList.remove('is-highlight');
      setTimeout(() => {
        window.addEventListener('scroll', stop, { passive: true, once: true });
        submitBtn.addEventListener('mouseenter', stop, { once: true });
        submitBtn.addEventListener('focus', stop, { once: true });
        submitBtn.addEventListener('click', stop, { once: true });
      }, 1800);
    }

    /**
     * @param {string} id
     * @param {string} value
     */
    function setFieldValue(id, value) {
      const el = /** @type {HTMLInputElement | HTMLTextAreaElement | null} */ (root.querySelector(`#${id}`));
      if (el) el.value = value;
    }

    function saveDraft() {
      if (!techForm) return;
      const fd = new FormData(techForm);
      const draft = {
        title: String(fd.get('title') || ''),
        category_id: categorySelect ? categorySelect.value : '',
        body: String(fd.get('body') || ''),
        notify: fd.get('notify') === 'on',
        consent: fd.get('consent') === 'on',
        file_names: selectedFiles.map((f) => f.name),
        saved_at: Date.now(),
      };
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      } catch (_) { /* ignore quota errors */ }
    }

    /**
     * @returns {{ title?: string, category_id?: string, body?: string, notify?: boolean, consent?: boolean, file_names?: string[], saved_at: number } | null}
     */
    function readDraft() {
      try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.saved_at !== 'number') return null;
        if (Date.now() - parsed.saved_at > DRAFT_TTL_MS) {
          localStorage.removeItem(DRAFT_KEY);
          return null;
        }
        return parsed;
      } catch (_) { return null; }
    }

    function clearDraft() {
      try { localStorage.removeItem(DRAFT_KEY); } catch (_) { /* ignore */ }
    }

    /* ---------- file input ---------- */

    if (fileInput) {
      fileInput.addEventListener('change', () => {
        addFiles(Array.from(fileInput.files || []));
        fileInput.value = '';
      });
    }

    if (dropzone && fileInput) {
      ['dragenter', 'dragover'].forEach((evt) => {
        dropzone.addEventListener(evt, (e) => {
          e.preventDefault();
          dropzone.classList.add('is-dragover');
        });
      });
      ['dragleave', 'drop'].forEach((evt) => {
        dropzone.addEventListener(evt, (e) => {
          e.preventDefault();
          dropzone.classList.remove('is-dragover');
        });
      });
      dropzone.addEventListener('drop', (e) => {
        const dt = /** @type {DragEvent} */ (e).dataTransfer;
        if (dt && dt.files && dt.files.length) addFiles(Array.from(dt.files));
      });
    }

    /** @param {File[]} incoming */
    function addFiles(incoming) {
      clearFieldError('tech-attachments');
      for (const file of incoming) {
        if (selectedFiles.length >= MAX_FILES) {
          if (techForm) {
            showFieldError(techForm, 'tech-attachments', interpolate(ERROR_COPY.too_many_files, { count: MAX_FILES }));
          }
          break;
        }
        if (file.size > MAX_FILE_BYTES) {
          if (techForm) {
            showFieldError(techForm, 'tech-attachments', interpolate(ERROR_COPY.file_too_large_named, { filename: file.name }));
          }
          continue;
        }
        selectedFiles.push(file);
      }
      renderFileList();
    }

    /** @param {number} index */
    function removeFile(index) {
      selectedFiles.splice(index, 1);
      renderFileList();
    }

    function renderFileList() {
      if (!fileList) return;
      fileList.innerHTML = '';
      if (selectedFiles.length === 0) {
        fileList.hidden = true;
        return;
      }
      fileList.hidden = false;
      selectedFiles.forEach((file, i) => {
        const li = document.createElement('li');
        const name = document.createElement('span');
        name.textContent = `${file.name} (${formatBytes(file.size)})`;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = UI.file_remove;
        btn.addEventListener('click', () => removeFile(i));
        li.append(name, btn);
        fileList.appendChild(li);
      });
    }

    /** @param {number} n */
    function formatBytes(n) {
      if (n < 1024) return `${n} B`;
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
      return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    }

    /** @param {string} fieldId */
    function clearFieldError(fieldId) {
      const err = /** @type {HTMLElement | null} */ (root.querySelector(`[data-error-for="${fieldId}"]`));
      if (err) {
        err.hidden = true;
        err.textContent = '';
      }
      const field = root.querySelector(`#${fieldId}`);
      if (field) field.removeAttribute('aria-invalid');
    }

    /* ---------- submit handlers ---------- */

    if (techForm) techForm.addEventListener('submit', handleTechSubmit);
    if (stdForm) stdForm.addEventListener('submit', handleStdSubmit);

    /** @param {string} v */
    function isValidEmail(v) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    }

    /** @param {Event} e */
    async function handleTechSubmit(e) {
      e.preventDefault();
      if (!techForm) return;
      clearFormBanners();

      const titleEl = /** @type {HTMLInputElement} */ (techForm.querySelector('#tech-title'));
      const categoryEl = /** @type {HTMLSelectElement} */ (techForm.querySelector('#tech-category'));
      const bodyEl = /** @type {HTMLTextAreaElement} */ (techForm.querySelector('#tech-body'));
      const notifyEl = /** @type {HTMLInputElement} */ (techForm.querySelector('#tech-notify'));
      const consentEl = /** @type {HTMLInputElement} */ (techForm.querySelector('#tech-consent'));

      const title = titleEl.value.trim();
      const categoryId = categoryEl.value;
      const body = bodyEl.value.trim();
      const notify = notifyEl.checked;
      const consent = consentEl.checked;

      let valid = true;
      if (!title) { showFieldError(techForm, 'tech-title', ERROR_COPY.title_required); valid = false; }
      if (!categoryId) { showFieldError(techForm, 'tech-category', ERROR_COPY.category_id_required); valid = false; }
      if (!body) { showFieldError(techForm, 'tech-body', ERROR_COPY.body_required); valid = false; }
      if (!consent) { showFieldError(techForm, 'tech-consent', ERROR_COPY.consent_required); valid = false; }
      if (selectedFiles.length > MAX_FILES) {
        showFieldError(techForm, 'tech-attachments', interpolate(ERROR_COPY.too_many_files, { count: MAX_FILES }));
        valid = false;
      }
      for (const f of selectedFiles) {
        if (f.size > MAX_FILE_BYTES) {
          showFieldError(techForm, 'tech-attachments', interpolate(ERROR_COPY.file_too_large_named, { filename: f.name }));
          valid = false;
        }
      }
      if (!valid) return;

      const fd = new FormData();
      fd.append('title', title);
      fd.append('category_id', categoryId);
      fd.append('body', body);
      fd.append('notify', notify ? 'true' : 'false');
      fd.append('consent', consent ? 'true' : 'false');
      selectedFiles.forEach((f) => fd.append('files', f, f.name));

      const turnstileToken = readTurnstileToken(techForm);
      if (turnstileToken) fd.append('turnstile_token', turnstileToken);

      const submitBtn = /** @type {HTMLButtonElement | null} */ (techForm.querySelector('[data-submit]'));
      setLoading(submitBtn, true, UI.tech_submit_loading);

      try {
        const res = await fetch(`${bridge}/api/contact/technical`, {
          method: 'POST',
          credentials: 'include',
          body: fd,
        });

        if (res.status === 401) {
          saveDraft();
          const here = `${location.origin}${location.pathname}?from=login`;
          location.href = `${bridge}/login?return_to=${encodeURIComponent(here)}`;
          return;
        }

        const data = await safeJson(res);

        if (res.ok && data && data.ok) {
          clearDraft();
          if (data.fallback) {
            showPanel('tech-fallback');
          } else {
            if (topicLink && typeof data.topic_url === 'string') topicLink.href = data.topic_url;
            else if (topicLink) topicLink.href = communityUrl;
            showPanel('tech-success');
          }
          resetTechForm();
          return;
        }

        const errCode = data && typeof data.error === 'string' ? data.error : null;
        const messages = data && Array.isArray(data.messages)
          ? /** @type {unknown[]} */ (data.messages).filter((m) => typeof m === 'string')
          : [];
        if (errCode === 'file_too_large' && data && typeof data.filename === 'string') {
          showFieldError(techForm, 'tech-attachments', interpolate(ERROR_COPY.file_too_large_named, { filename: data.filename }));
        } else if (errCode === 'title_required') {
          showFieldError(techForm, 'tech-title', ERROR_COPY.title_required);
        } else if (errCode === 'body_required') {
          showFieldError(techForm, 'tech-body', ERROR_COPY.body_required);
        } else if (errCode === 'category_id_required' || errCode === 'category_not_found') {
          showFieldError(techForm, 'tech-category', mapError(errCode));
        } else if (errCode === 'consent_required') {
          showFieldError(techForm, 'tech-consent', ERROR_COPY.consent_required);
        } else if (errCode === 'discourse_validation_failed') {
          const text = messages.length ? messages.join('. ') : ERROR_COPY.discourse_validation_failed;
          showFormBanner(techForm, text);
        } else if (errCode === 'discourse_rate_limited') {
          const tail = messages.length ? ` ${messages.join(' ')}` : '';
          showFormBanner(techForm, `${ERROR_COPY.discourse_rate_limited}${tail}`);
        } else {
          showFormBanner(techForm, mapError(errCode, data));
        }
      } catch (_err) {
        showFormBanner(techForm, ERROR_COPY.network);
      } finally {
        setLoading(submitBtn, false, UI.tech_submit);
      }
    }

    /** @param {Event} e */
    async function handleStdSubmit(e) {
      e.preventDefault();
      if (!stdForm) return;
      clearFormBanners();

      const topicEl = /** @type {HTMLSelectElement} */ (stdForm.querySelector('#std-topic'));
      const nameEl = /** @type {HTMLInputElement} */ (stdForm.querySelector('#std-name'));
      const companyEl = /** @type {HTMLInputElement} */ (stdForm.querySelector('#std-company'));
      const emailEl = /** @type {HTMLInputElement} */ (stdForm.querySelector('#std-email'));
      const orderEl = /** @type {HTMLInputElement} */ (stdForm.querySelector('#std-order'));
      const messageEl = /** @type {HTMLTextAreaElement} */ (stdForm.querySelector('#std-message'));
      const consentEl = /** @type {HTMLInputElement} */ (stdForm.querySelector('#std-consent'));

      const topic = topicEl.value;
      const name = nameEl.value.trim();
      const company = companyEl.value.trim();
      const email = emailEl.value.trim();
      const orderNumber = orderEl.value.trim();
      const message = messageEl.value.trim();
      const consent = consentEl.checked;

      let valid = true;
      if (!topic) { showFieldError(stdForm, 'std-topic', ERROR_COPY.invalid_topic); valid = false; }
      if (!name) { showFieldError(stdForm, 'std-name', ERROR_COPY.name_required); valid = false; }
      if (!email || !isValidEmail(email)) { showFieldError(stdForm, 'std-email', ERROR_COPY.invalid_email); valid = false; }
      if (!message) { showFieldError(stdForm, 'std-message', ERROR_COPY.message_required); valid = false; }
      if (!consent) { showFieldError(stdForm, 'std-consent', ERROR_COPY.consent_required); valid = false; }
      if (!valid) return;

      /** @type {Record<string, string | boolean>} */
      const payload = { topic, name, email, message, consent: true };
      if (company) payload.company = company;
      if (orderNumber) payload.order_number = orderNumber;
      const turnstileToken = readTurnstileToken(stdForm);
      if (turnstileToken) payload.turnstile_token = turnstileToken;

      const submitBtn = /** @type {HTMLButtonElement | null} */ (stdForm.querySelector('[data-submit]'));
      setLoading(submitBtn, true, UI.std_submit_loading);

      try {
        const res = await fetch(`${bridge}/api/contact/standard`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await safeJson(res);

        if (res.ok && data && data.ok) {
          showPanel('other-success');
          stdForm.reset();
          const newConsent = /** @type {HTMLInputElement | null} */ (stdForm.querySelector('#std-consent'));
          if (newConsent) newConsent.checked = true;
          return;
        }

        const errCode = data && typeof data.error === 'string' ? data.error : null;
        if (errCode === 'invalid_topic') showFieldError(stdForm, 'std-topic', ERROR_COPY.invalid_topic);
        else if (errCode === 'name_required') showFieldError(stdForm, 'std-name', ERROR_COPY.name_required);
        else if (errCode === 'invalid_email') showFieldError(stdForm, 'std-email', ERROR_COPY.invalid_email);
        else if (errCode === 'message_required') showFieldError(stdForm, 'std-message', ERROR_COPY.message_required);
        else if (errCode === 'consent_required') showFieldError(stdForm, 'std-consent', ERROR_COPY.consent_required);
        else showFormBanner(stdForm, mapError(errCode, data));
      } catch (_err) {
        showFormBanner(stdForm, ERROR_COPY.network);
      } finally {
        setLoading(submitBtn, false, UI.std_submit);
      }
    }

    function resetTechForm() {
      if (!techForm) return;
      techForm.reset();
      selectedFiles = [];
      renderFileList();
      const notify = /** @type {HTMLInputElement | null} */ (techForm.querySelector('#tech-notify'));
      if (notify) notify.checked = true;
      const consent = /** @type {HTMLInputElement | null} */ (techForm.querySelector('#tech-consent'));
      if (consent) consent.checked = true;
      if (draftBanner) draftBanner.hidden = true;
    }

    /**
     * @param {HTMLButtonElement | null} btn
     * @param {boolean} loading
     * @param {string} [label]
     */
    function setLoading(btn, loading, label) {
      if (!btn) return;
      btn.disabled = loading;
      btn.classList.toggle('is-loading', loading);
      if (label) btn.textContent = label;
    }

    /** @param {HTMLFormElement} form */
    function readTurnstileToken(form) {
      const el = /** @type {HTMLInputElement | null} */ (form.querySelector('[name="cf-turnstile-response"]'));
      return el ? el.value : '';
    }

    /** @param {Response} res */
    async function safeJson(res) {
      try { return await res.json(); } catch (_) { return null; }
    }
  }
})();
